import { shipment, shipmentSettingsFunc } from '../../../../models/entities/shipment/shipment';
import { PagesRoutes } from '../../../../routing/PagesRoutes';
import CellTrigger from '../../../shared/Menu/MenuTriggers/CellTrigger';
import StyledLink from '../../../shared/SmallComponents/StyledLink';
import { styled } from '../../../shared/Theme/theme';
import { getShipmentPieces, getStatusPillStyle } from './consolidationUtils';

type Props = {
    shipments: shipment[];
    settings: shipmentSettingsFunc;
    totalPieces: number;
    totalWeight: number;
    totalVolume: number;
};

const HouseShipmentsTable = ({ shipments, settings, totalPieces, totalWeight, totalVolume }: Props) => {
    const defaultSettings = settings(shipments[0]);

    const resolveState = (row: shipment) => {
        const rowSettings = settings(row) || defaultSettings;
        const stateType = rowSettings?.state?.find((item) => item.code === row.state);
        const label = stateType?.name || row.state || '-';
        const pillStyle = getStatusPillStyle(stateType?.color);
        return { label, pillStyle };
    };

    const houseAwbLabel = (row: shipment) => {
        const label = row.house && row.house !== '-' ? row.house : row.CargoZoneNumber || '-';
        return label;
    };

    return (
        <TableWrap>
            <Table>
                <thead>
                    <tr>
                        <Th $width="18%">House AWB</Th>
                        <Th $width="16%">Shipper</Th>
                        <Th $width="16%">Consignee</Th>
                        <Th $width="8%" $align="center">
                            Pieces
                        </Th>
                        <Th $width="10%" $align="center">
                            Weight (KG)
                        </Th>
                        <Th $width="10%" $align="center">
                            Volume (CBM)
                        </Th>
                        <Th $width="12%" $align="center">
                            Commodity
                        </Th>
                        <Th $width="12%">Status</Th>
                        <Th $width="8%" $align="center">
                            Actions
                        </Th>
                    </tr>
                </thead>
                <tbody>
                    {shipments.map((row) => {
                        const { label: statusLabel, pillStyle } = resolveState(row);
                        const pieces = getShipmentPieces(row);
                        return (
                            <tr key={row.id}>
                                <Td>
                                    <StyledLink to={`${PagesRoutes.Shipments}/${row.id}`}>{houseAwbLabel(row)}</StyledLink>
                                </Td>
                                <Td>{row.shipperName || '-'}</Td>
                                <Td>{row.consigneeName || '-'}</Td>
                                <Td $align="center">{pieces != null ? pieces : '-'}</Td>
                                <Td $align="center">{row.weight ?? '-'}</Td>
                                <Td $align="center">{row.volume ?? '-'}</Td>
                                <Td $align="center">{row.commodity || '-'}</Td>
                                <Td>
                                    <StatusPill $background={pillStyle.background} $color={pillStyle.color}>
                                        {statusLabel}
                                    </StatusPill>
                                </Td>
                                <Td $align="center">
                                    <ActionsCell>
                                        <CellTrigger
                                            icon="more_horiz"
                                            isOpen={false}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        />
                                    </ActionsCell>
                                </Td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <Tf colSpan={3}>Total</Tf>
                        <Tf $align="center">{totalPieces || '-'}</Tf>
                        <Tf $align="center">{totalWeight}</Tf>
                        <Tf $align="center">{totalVolume}</Tf>
                        <Tf colSpan={3} />
                    </tr>
                </tfoot>
            </Table>
        </TableWrap>
    );
};

const TableWrap = styled.div`
    width: 100%;
    overflow-x: auto;
    overflow-y: visible;
    max-height: none;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: ${(props) => props.theme.fontSizes.gridCell};
    color: ${(props) => props.theme.colors.gridCell};
`;

const Th = styled.th<{ $width?: string; $align?: string }>`
    width: ${(props) => props.$width || 'auto'};
    padding: 10px 12px;
    text-align: ${(props) => props.$align || 'left'};
    font-weight: 500;
    font-size: 13px;
    color: ${(props) => props.theme.colors.gridResultsCounter};
    background: #f5f6f8;
    border-top: 1px solid #e8e8e8;
    border-bottom: none;
    vertical-align: middle;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Td = styled.td<{ $align?: string }>`
    padding: 12px;
    text-align: ${(props) => props.$align || 'left'};
    vertical-align: middle;
    border-bottom: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Tf = styled.td<{ $align?: string }>`
    padding: 12px;
    text-align: ${(props) => props.$align || 'left'};
    font-weight: 700;
    color: ${(props) => props.theme.colors.pageTitle};
    border-top: 1px solid #e8e8e8;
    vertical-align: middle;
`;

const StatusPill = styled.span<{ $background: string; $color: string }>`
    display: inline-block;
    border-radius: 12px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
    background: ${(props) => props.$background};
    color: ${(props) => props.$color};
`;

const ActionsCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
`;

export default HouseShipmentsTable;
